import { useState, useEffect, useCallback } from 'react';
import { Check, ChevronsUpDown, Search, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';

export interface TeacherInfo {
  code: string;
  nickname: string;
  firstName: string;
  lastName: string;
}

interface TeacherOption {
  value: string;
  label: string;
  teacher: TeacherInfo;
}

interface TeacherSearchSelectProps {
  value: string;
  onChange: (code: string, teacher: TeacherInfo | null) => void;
}

export function TeacherSearchSelect({ value, onChange }: TeacherSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState<TeacherOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherInfo | null>(null);

  // Load initial teacher if value is provided
  useEffect(() => {
    if (value && !selectedTeacher) {
      loadTeacherByCode(value);
    }
  }, [value]);

  const loadTeacherByCode = async (code: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('search-teachers', {
        body: { query: code }
      });

      if (!error && data?.teachers?.length > 0) {
        const teacher = data.teachers.find((t: any) => t.code === code);
        if (teacher) {
          setSelectedTeacher({
            code: teacher.code,
            nickname: teacher.nickname || '-',
            firstName: teacher.first_name || teacher.name?.split(' ')[0] || '-',
            lastName: teacher.last_name || teacher.name?.split(' ').slice(1).join(' ') || '-',
          });
        }
      }
    } catch (error) {
      console.error('Error loading teacher:', error);
    }
  };

  const searchTeachers = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('search-teachers', {
        body: { query }
      });

      if (error) {
        console.error('Error searching teachers:', error);
        setOptions([]);
        return;
      }

      const teacherOptions: TeacherOption[] = (data?.teachers || []).map((teacher: any) => ({
        value: teacher.code,
        label: `${teacher.code} - ${teacher.name}`,
        teacher: {
          code: teacher.code,
          nickname: teacher.nickname || '-',
          firstName: teacher.first_name || teacher.name?.split(' ')[0] || '-',
          lastName: teacher.last_name || teacher.name?.split(' ').slice(1).join(' ') || '-',
        }
      }));

      setOptions(teacherOptions);
    } catch (error) {
      console.error('Error in teacher search:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchTeachers(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchTeachers]);

  const handleSelect = (option: TeacherOption) => {
    setSelectedTeacher(option.teacher);
    onChange(option.value, option.teacher);
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = () => {
    setSelectedTeacher(null);
    onChange('', null);
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            {value ? (
              <span>{value} - {selectedTeacher?.firstName || ''}</span>
            ) : (
              <span className="text-muted-foreground">ค้นหาครู...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="พิมพ์รหัสหรือชื่อครู..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="ml-2 text-sm">กำลังค้นหา...</span>
                </div>
              ) : searchQuery.length < 2 ? (
                <CommandEmpty>พิมพ์อย่างน้อย 2 ตัวอักษร</CommandEmpty>
              ) : options.length === 0 ? (
                <CommandEmpty>ไม่พบครู</CommandEmpty>
              ) : (
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => handleSelect(option)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="font-medium">{option.value}</span>
                        <span className="text-sm text-muted-foreground">
                          {option.teacher.nickname} - {option.teacher.firstName} {option.teacher.lastName}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedTeacher && (
        <div className="p-3 bg-muted rounded-md text-sm space-y-1">
          <p><span className="font-medium">รหัส:</span> {selectedTeacher.code}</p>
          <p><span className="font-medium">ชื่อเล่น:</span> {selectedTeacher.nickname}</p>
          <p>
            <span className="font-medium">ชื่อ:</span> {selectedTeacher.firstName}{' '}
            <span className="font-medium">นามสกุล:</span> {selectedTeacher.lastName}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 h-7 text-xs"
            onClick={handleClear}
          >
            ล้างการเลือก
          </Button>
        </div>
      )}
    </div>
  );
}
